import * as React from 'react';
import { useQueryClient } from 'react-query';
import { useApiUrl } from '../../../../../../../shared/hooks/useApiUrl';
import ImageProcessingApi from '../../../api/ImageProcessingApi';
import { preloadImage } from '../../../cache';
import { EditedImageDetails, getEditedImageQueryKey } from './use-query-edited-image';

const PREFETCHED_IMAGE_STALE_TIME_MILLISECONDS = 5 * 60 * 1000;

type UsePrefetchAdjacentEditedImagesParams = {
    orderId: number;
    imageId: number;
    imageIds: number[];
    enabled: boolean;
};

export function usePrefetchAdjacentEditedImages({
    orderId,
    imageId,
    imageIds,
    enabled
}: UsePrefetchAdjacentEditedImagesParams) {
    let apiUrl = useApiUrl();
    let queryClient = useQueryClient();
    let index = imageIds.indexOf(imageId);
    let previousImageId = index > 0 ? imageIds[index - 1] : null;
    let nextImageId = index !== -1 && index < imageIds.length - 1 ? imageIds[index + 1] : null;

    React.useEffect(
        function prefetchAdjacentImages() {
            if (!enabled) {
                return;
            }

            [nextImageId, previousImageId]
                .filter((adjacentImageId) => adjacentImageId !== null)
                .forEach((adjacentImageId) => {
                    let queryKey = getEditedImageQueryKey(orderId, adjacentImageId);

                    queryClient
                        .prefetchQuery(
                            queryKey,
                            () => ImageProcessingApi.getEditedImage(orderId, adjacentImageId),
                            { staleTime: PREFETCHED_IMAGE_STALE_TIME_MILLISECONDS }
                        )
                        .then(() => {
                            let details = queryClient.getQueryData<EditedImageDetails>(queryKey);
                            let versions = details ? details.imageShowcaseViewModels : [];
                            let latestVersion = versions[versions.length - 1];

                            if (latestVersion) {
                                preloadImage(`${apiUrl}/file/${latestVersion.reference}`);
                            }
                        });
                });
        },
        [enabled, orderId, previousImageId, nextImageId, queryClient, apiUrl]
    );
}
